import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { PrismTooltip } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import { camelize } from 'frontend-utils/utils/convert-case';
import type * as Highcharts from 'highcharts';
import cloneDeep from 'lodash.clonedeep';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelAndMarketRevenueInsightBasePaceBase, {
  type HotelAndMarketRevenueInsightBasePaceBaseSignature,
} from 'lumen/components/hotel-and-market/revenue-insight/base/pace-base';
import HotelAndMarketRevenueInsightTooltipsTooltipPace from 'lumen/components/hotel-and-market/revenue-insight/tooltips/tooltip-pace';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import { addZone } from 'lumen/utils/charts/zones';
import {
  CHART_CANCELLATIONS_PATTERN,
  INVALID_RESERVATIONS_MESSAGE,
  type KpiCategory,
  type KPIS,
} from 'lumen/utils/constants/ri-constant-values';
import { getZoneStart } from 'lumen/utils/revenue-insight/ri-utils';
import { timeout } from 'lumen/utils/waiters';

interface DataPoint {
  x: number;
  y: number | null;
}

interface ExtendedTooltip extends Highcharts.Tooltip {
  getPosition: (
    labelWidth: number,
    labelHeight: number,
    point: Highcharts.Point | Highcharts.TooltipPositionerPointObject,
  ) => Highcharts.PositionObject;
}

type SupportedKpi =
  | typeof KPIS.NR_OF_ROOMS
  | typeof KPIS.REVENUE
  | typeof KPIS.ADR;

interface HotelAndMarketRevenueInsightChartsChartPaceSignature extends HotelAndMarketRevenueInsightBasePaceBaseSignature {
  Args: HotelAndMarketRevenueInsightBasePaceBaseSignature['Args'] & {
    allSegmentation: [RevenueInsightSegmentationValue];
    isLoading?: boolean;
    kpi: SupportedKpi;
    kpiCategory: KpiCategory;
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    visibleKpis: SupportedKpi[];
  };
}

export default class HotelAndMarketRevenueInsightChartsChartPace extends HotelAndMarketRevenueInsightBasePaceBase<HotelAndMarketRevenueInsightChartsChartPaceSignature> {
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Show budget series
  @tracked isBudgetVisible = true;
  // Show Cancellations series
  @tracked isCancellationsVisible = false;
  // Show forecast series
  @tracked isForecastVisible = true;
  // Show last years final series
  @tracked isLastYearFinalVisible = true;
  // Show the changes series
  @tracked isLastYearVisible = true;
  @tracked isPickupLastYearVisible = true;
  // Show the this year series
  @tracked isThisYearVisible = true;

  performanceComparisonTrackingTask = task(
    { drop: true },
    async (): Promise<void> => {
      await timeout('extra-long');

      this.usageAnalytics.track('KPI comparison viewed', {
        eventTrigger: 'current-vs-last-year-performance',
      });
    },
  );
  tooltipHoverTrackingTask = task({ drop: true }, async (): Promise<void> => {
    await timeout('extra-long');

    this.usageAnalytics.track('Pace Hover Interaction', {
      eventTrigger: 'pace-chart-hover',
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get budgetForSelectedKpi() {
    const { kpi, kpiCategory } = this.args;
    return this.budgetValue?.[camelize(`${kpi}_${kpiCategory}`)] ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get budgetValue() {
    return this.args.allSegmentation?.[0]?.calculatedValues?.budget;
  }

  // Preserve reference for Highcharts which receives this via @model
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const adaptedData = [];

    let lastYearSeries = null;
    let cancellationSeries = null;
    let lastYearFinalSeries = null;
    let budgetSeries = null;
    let forecastSeries = null;

    if (this.riSelections.hasCancellations) {
      cancellationSeries = {
        name: this.intl.t('revenue_insight.legend.cancellations').toString(),
        data: [] as DataPoint[],
        zones: [],
        visible: this.isCancellationsVisible,
        marker: { enabled: false },
        zoneAxis: 'x',
        type: 'area',
        fillColor: CHART_CANCELLATIONS_PATTERN,
        fillOpacity: 1, // look like a line, but needs to be an area for the cancellations
        color: 'var(--prism-color-chart-status-figure-critical-normal)',
        lineWidth: 3,
      };

      adaptedData.push(cancellationSeries);
    }

    const thisYearSeries = {
      name: this.intl.t('revenue_insight.this_year').toString(),
      data: [] as DataPoint[],
      zones: [],
      visible: this.isThisYearVisible,
      marker: { enabled: false },
      zoneAxis: 'x',
      type: 'area',
      zIndex: 10,
      opacity: 1,
      color: 'var(--prism-color-chart-accent-primary-figure-default)',
      fillColor: 'transparent',
      lineWidth: 3,
    };

    adaptedData.push(thisYearSeries);

    lastYearSeries = {
      id: 'lastYear',
      name: this.intl.t('revenue_insight.last_year').toString(),
      data: [] as DataPoint[],
      visible: this.isLastYearVisible,
      marker: { enabled: false },
      color: 'var(--prism-color-chart-accent-primary-figure-subdued)',
      lineWidth: 3,
    };

    adaptedData.push(lastYearSeries);

    if (this.isBudgetEnabled) {
      budgetSeries = {
        id: 'budget',
        name: this.intl.t('revenue_insight.budget').toString(),
        visible: this.isBudgetVisible,
        data: [] as DataPoint[],
        marker: { enabled: false },
        color: 'var(--prism-color-chart-accent-secondary-figure-emphasis)',
        lineWidth: 3,
      };

      adaptedData.push(budgetSeries);
    } else if (this.isForecastEnabled) {
      forecastSeries = {
        id: 'forecast',
        name: this.intl.t('general.forecast').toString(),
        visible: this.isForecastVisible,
        data: [] as DataPoint[],
        marker: { enabled: false },
        color: 'var(--prism-color-chart-accent-secondary-figure-emphasis)',
        lineWidth: 3,
      };

      adaptedData.push(forecastSeries);
    } else {
      // If budget is not active and there's last year data, only then show last year's final
      lastYearFinalSeries = {
        id: 'lastYearFinal',
        name: this.riSelections?.referenceFinalText.toString(),
        data: [] as DataPoint[],
        visible: this.isLastYearFinalVisible,
        marker: { enabled: false },
        color: 'var(--prism-color-general-neutral-600)',
        lineWidth: 3,
      };

      adaptedData.push(lastYearFinalSeries);
    }

    // Create a unix ms timestamp below that defines the start of a dotted line zone
    // This represents forecasted values
    let zoneStart: number | null = null;

    this.sortedData.forEach((dataEntry) => {
      const xDate = new Date(dataEntry.segmentationValue);
      const xValue = xDate.getTime();

      // If the data point is after today, we want to show last year's data, but as a dotted line
      // We don't want to see this when viewing ADR, then we want the old view
      if (
        dataEntry.calculatedValues.forecastReference[this.args.kpi] !== null &&
        !this.isPickupLastYearVisible
      ) {
        thisYearSeries.data.push({
          x: xValue,
          y: null,
        });
      } else if (
        dataEntry.calculatedValues.forecastReference[this.args.kpi] !== null
      ) {
        thisYearSeries.data.push({
          x: xValue,
          y:
            dataEntry.calculatedValues.forecastReference[this.args.kpi] ?? null,
        });

        if (!zoneStart) {
          zoneStart = getZoneStart(xValue);
        }
      } else if (
        dataEntry.value &&
        dataEntry.value.message !== INVALID_RESERVATIONS_MESSAGE
      ) {
        thisYearSeries.data.push({
          x: xValue,
          y: dataEntry.calculatedValues.BOB[this.args.kpi] ?? null,
        });
      }

      if (
        this.riSelections.hasCancellations &&
        dataEntry.calculatedValues.cancellationBOB &&
        cancellationSeries
      ) {
        cancellationSeries.data.push({
          x: xValue,
          y: dataEntry.calculatedValues.cancellationBOB[this.args.kpi] ?? null,
        });
      }

      if (
        dataEntry.calculatedValues.reference &&
        lastYearSeries &&
        dataEntry.referenceValue &&
        dataEntry.referenceValue.message !== INVALID_RESERVATIONS_MESSAGE
      ) {
        lastYearSeries.data.push({
          x: xValue,
          y: dataEntry.calculatedValues.reference[this.args.kpi] ?? null,
        });
      }

      if (
        this.lastYearFinalValue &&
        lastYearFinalSeries &&
        !this.isBudgetEnabled &&
        !this.isForecastEnabled
      ) {
        lastYearFinalSeries.data.push({
          x: xValue,
          y: this.lastYearFinalValue,
        });
      }

      if (this.isBudgetEnabled && budgetSeries) {
        budgetSeries.data.push({
          x: xValue,
          y: this.budgetForSelectedKpi,
        });
      }

      if (this.isForecastEnabled && forecastSeries) {
        forecastSeries.data.push({
          x: xValue,
          y: this.forecastForSelectedKpi,
        });
      }
    });

    // Define a zone on this year to be solid until today...
    addZone(thisYearSeries.zones, zoneStart, { dashStyle: 'Solid' });
    // ...fill the rest of the chart (future) with dots
    addZone(thisYearSeries.zones, null, {
      dashStyle: 'ShortDot',
      color: 'var(--prism-color-chart-accent-primary-figure-subdued)',
    });

    return adaptedData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const chartOptions = cloneDeep(
      this.baseChartOptions,
    ) as unknown as Highcharts.Options;

    chartOptions.legend = {
      enabled: false,
    };

    if (!chartOptions.tooltip) {
      return chartOptions;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    chartOptions.tooltip.positioner = function (
      labelWidth,
      labelHeight,
      point,
    ) {
      const hasPrismTooltipFooter = Boolean(
        document.querySelector(
          '#dummy-div-for-tooltip-pace-chart [data-test-id="tooltip-pace-header"]',
        ),
      );

      const PRISM_TOOLTIP_FOOTER_HEIGHT = 35;

      const correctedLabelHeight = hasPrismTooltipFooter
        ? labelHeight + PRISM_TOOLTIP_FOOTER_HEIGHT
        : labelHeight;

      // Use Highcharts default positioner with the corrected label height
      // eslint-disable-next-linep prefer-spread
      return (this as ExtendedTooltip).getPosition.apply(this, [
        labelWidth,
        correctedLabelHeight,
        point,
      ]);
    };

    const seriesPointEvents =
      chartOptions.plotOptions?.series?.point?.events ?? null;

    if (!seriesPointEvents) {
      return chartOptions;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    seriesPointEvents.mouseOver = function (this: Highcharts.Point) {
      void that.tooltipHoverTrackingTask.perform();

      const zones = that.chartData[1]?.zones as { value?: number }[];
      const zoneXValue = zones?.[0]?.value; // this is always the this year series
      const xValue = this.x;

      if (xValue && zoneXValue && xValue > zoneXValue) {
        void that.performanceComparisonTrackingTask.perform();
      }
    };

    return chartOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentPeriodText() {
    return this.riSelections?.currentPeriodText;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get forecastForSelectedKpi() {
    const { kpi, kpiCategory } = this.args;
    return this.forecastValue?.[camelize(`${kpi}_${kpiCategory}`)] ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get forecastValue() {
    return this.args.allSegmentation?.[0]?.calculatedValues?.forecast;
  }

  get hasLastYearFinalValue(): boolean {
    return this.lastYearFinalValue !== null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBudgetEnabled() {
    return this.riSelections?.isBudgetActive;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isForecastEnabled() {
    return this.riSelections?.isForecastActive;
  }

  get isLastYearFinalLegendItemHidden(): boolean {
    return !this.hasLastYearFinalValue || !this.isLastYearFinalVisible;
  }

  get isLastYearLegendItemHidden(): boolean {
    return !this.hasLastYearFinalValue || !this.isLastYearVisible;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lastYearFinalValue() {
    // Get the last year's final data point directly from the sorted data
    const lastYearFinalDataPoint = this.sortedData[this.sortedData.length - 1];
    let lastYearFinalValue = null;
    if (
      lastYearFinalDataPoint &&
      lastYearFinalDataPoint.referenceValue &&
      lastYearFinalDataPoint.referenceValue.message !==
        INVALID_RESERVATIONS_MESSAGE
    ) {
      lastYearFinalValue = lastYearFinalDataPoint.referenceValue[this.args.kpi];
    }

    return lastYearFinalValue;
  }

  get referencePeriodText(): string {
    return this.riSelections.getReferencePeriodText(true);
  }

  get showPickupLastYear(): boolean {
    return this.sortedData.some(
      (item) => item.calculatedValues.forecastReference[this.args.kpi] !== null,
    );
  }

  /**
   * Toggles the visiblity of series
   * @param  { string } property : the property that handles the visibility of the series
   */
  @action
  toggleSeries(
    property:
      | 'isThisYearVisible'
      | 'isCancellationsVisible'
      | 'isLastYearVisible'
      | 'isBudgetVisible'
      | 'isForecastVisible'
      | 'isLastYearFinalVisible'
      | 'isPickupLastYearVisible',
  ): void {
    this[property] = !this[property];

    if (property === 'isThisYearVisible' && !this[property]) {
      this.isPickupLastYearVisible = false;
    }

    if (
      property === 'isPickupLastYearVisible' &&
      this[property] &&
      !this.isThisYearVisible
    ) {
      this.isThisYearVisible = !this.isThisYearVisible;
    }

    if (property === 'isCancellationsVisible') {
      this.isLastYearVisible = !this.isCancellationsVisible;
      this.isLastYearFinalVisible = !this.isCancellationsVisible;
    }
  }

  <template>
    <div>
      <GeneralChartsOtaChart
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
        @onUpdateChartReference={{@onUpdateChartReference}}
      />
      {{#unless @isLoading}}
        <div class="pace chart-legend mt-400">
          <GeneralChartsLegendsLegendItem
            @id="current"
            @isHidden={{not this.isThisYearVisible}}
            @legendItemClicked={{fn this.toggleSeries "isThisYearVisible"}}
            @type="line"
          >
            {{this.currentPeriodText}}
          </GeneralChartsLegendsLegendItem>
          {{#if this.riSelections.hasCancellations}}
            <GeneralChartsLegendsLegendItem
              @id="cancellations"
              @isHidden={{not this.isCancellationsVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isCancellationsVisible"
              }}
              @type="dot"
            >
              {{t "revenue_insight.legend.cancellations"}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
          <GeneralChartsLegendsLegendItem
            @id="previous"
            @isHidden={{this.isLastYearLegendItemHidden}}
            @legendItemClicked={{fn this.toggleSeries "isLastYearVisible"}}
            @type="line"
          >
            {{this.referencePeriodText}}
            {{#unless this.hasLastYearFinalValue}}
              <PrismTooltip
                @text={{t
                  "revenue_insight.pickup_and_pace.incomplete"
                  integrationDate=this.riSelections.formattedIntegrationDate
                }}
              />
            {{/unless}}
          </GeneralChartsLegendsLegendItem>
          {{#if (or this.isBudgetEnabled this.isForecastEnabled)}}
            {{#if this.isBudgetEnabled}}
              <GeneralChartsLegendsLegendItem
                @id="budget"
                @isHidden={{not this.isBudgetVisible}}
                @legendItemClicked={{fn this.toggleSeries "isBudgetVisible"}}
                @type="line"
              >
                {{t "revenue_insight.budget"}}
              </GeneralChartsLegendsLegendItem>
            {{else}}
              <GeneralChartsLegendsLegendItem
                @id="forecast"
                @isHidden={{not this.isForecastVisible}}
                @legendItemClicked={{fn this.toggleSeries "isForecastVisible"}}
                @type="line"
              >
                {{t "general.forecast"}}
              </GeneralChartsLegendsLegendItem>
            {{/if}}
          {{else}}
            <GeneralChartsLegendsLegendItem
              @id="final"
              @isHidden={{this.isLastYearFinalLegendItemHidden}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isLastYearFinalVisible"
              }}
              @type="line"
            >
              {{this.riSelections.referenceFinalText}}
              {{#unless this.hasLastYearFinalValue}}
                <PrismTooltip
                  @text={{t
                    "revenue_insight.pickup_and_pace.incomplete"
                    integrationDate=this.riSelections.formattedIntegrationDate
                  }}
                />
              {{/unless}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
          {{#if this.showPickupLastYear}}
            <GeneralChartsLegendsLegendItem
              @id="pickup-ly-visible"
              @isHidden={{not this.isPickupLastYearVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isPickupLastYearVisible"
              }}
              @type="line"
            >
              {{this.riSelections.leftToPickupText}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
        </div>
      {{/unless}}
      <div hidden id="dummy-div-for-tooltip-pace-chart">
        <div class="revenue-insight-segmentation-value-tooltip">
          {{! added if here since this tooltip has some instensive calculations and I wanted to avoid doing them if there was no data}}
          {{#if this.tooltipData}}
            <HotelAndMarketRevenueInsightTooltipsTooltipPace
              @allSegmentation={{@allSegmentation}}
              @data={{this.tooltipData}}
              @isPickupLastYearVisible={{this.isPickupLastYearVisible}}
              @kpiCategory={{@kpiCategory}}
              @kpis={{@visibleKpis}}
              @metricKey={{@kpi}}
            />
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
